import { useEffect, useRef, useState } from 'react'
import NoneAnchor from 'components/NoneAnchor'
import FilterAddInput from 'features/dashboard/filter/FilterAddInput'
import { batch, useDispatch, useSelector } from 'react-redux'
import {
  clearFilters,
  deleteFilter,
  fetchFilterResources,
} from 'features/dashboard/filter/dashboardFilterSlice'
import useOutsideClick from 'utils/hooks/useOutsideClick'
import FilterMenuLayer from 'features/dashboard/filter/FilterMenuLayer'
import { FilterContext } from 'features/dashboard/filter/FilterContext'
import FilterFormLayer from 'features/dashboard/filter/FilterFormLayer'
import FilterChip from 'features/dashboard/filter/FilterChip'
import { FilterOperators } from 'features/dashboard/filter/FilterSpecs'
import { useDialog } from 'features/dialog/Dialog'
import FilterManageModal from 'features/dashboard/filter/FilterManageModal'
import FilterWrapper from 'features/dashboard/filter/FilterWrapper'
import { campaignTableActions } from 'features/dashboard/table/campaign/campaignTableSlice'
import { adGroupTableActions } from 'features/dashboard/table/adGroup/adGroupTableSlice'
import { keywordTableActions } from 'features/dashboard/table/keyword/keywordTableSlice'
import { creativeTableActions } from 'features/dashboard/table/creative/creativeTableSlice'
import { useConfirm } from 'features/dialog/Confirm'
import { failWithErrorResponse, success } from 'features/toast/Toast'
import FilterDisabledLayer from 'features/dashboard/filter/FilterDisabledLayer'

const DashboardFilter = () => {
  const dispatch = useDispatch()
  const {
    filter: { filters, savedFilters },
    table: {
      keywordTable: { editable },
    },
  } = useSelector(state => state.dashboard)
  const [addVisible, setAddVisible] = useState(false)
  const [isOverflow, setOverflow] = useState(false)
  const [value, setValue] = useState('')
  const [selectedMenu, setSelectedMenu] = useState()
  const [menuVisible, setMenuVisible] = useState(false)
  const [formVisible, setFormVisible] = useState(false)
  const [filterForm, setFilterForm] = useState({
    domain: 'campaign',
    key: 'name',
    data: {
      value: '',
      operator: FilterOperators.like.key,
    },
  })
  const [deleteTarget, setDeleteTarget] = useState({
    id: undefined,
    name: undefined,
  })
  const [hover, setHover] = useState(false)
  const [savedFiltersCopy, setSavedFiltersCopy] = useState([])
  const [DeleteConfirm, showDeleteConfirm] = useConfirm()
  const filterWrapRef = useRef()
  const inputRef = useRef()
  const menuLayerRef = useRef()
  const formLayerRef = useRef()
  const filterRef = useRef()

  useEffect(() => {
    dispatch(fetchFilterResources())
  }, [dispatch])

  const showFilterFormLayer = filter => {
    const index = filters.findIndex(
      ({ domain, key }) => domain === filter.domain && key === filter.key
    )
    if (index !== -1) {
      filterRef.current = filterWrapRef?.current?.childNodes[index]
      setFilterForm({
        ...filter,
        data: filters[index].data,
      })
    } else {
      filterRef.current = inputRef.current
      setFilterForm(filter)
    }
    setMenuVisible(false)
    setFormVisible(true)
  }

  const hideFilterFormLayer = () => {
    setFormVisible(false)
  }

  const showDeleteSavedFilterConfirm = ({ id, name }) => {
    setDeleteTarget({
      id,
      name,
    })
    setMenuVisible(false)
    showDeleteConfirm()
  }

  const clickRef = useOutsideClick(({ target }) => {
    const isMenuLayerClick =
      menuLayerRef.current && menuLayerRef.current.contains(target)
    const isFormLayerClick =
      formLayerRef.current && formLayerRef.current.contains(target)
    if (!isMenuLayerClick && !isFormLayerClick) {
      if (value.trim() === '') {
        setMenuVisible(false)
        setAddVisible(false)
      }
    }
  })

  useEffect(() => {
    if (clickRef.current) {
      clickRef.current.scrollTop = clickRef.current.scrollHeight
    }
  }, [addVisible, filters, clickRef])

  const [FilterManageDialog, showFilterManageDialog, hideFilterManageDialog] =
    useDialog()

  return (
    <FilterContext.Provider
      value={{
        value,
        setValue,
        selectedMenu,
        setSelectedMenu,
        menuVisible,
        setMenuVisible,
        formVisible,
        showFilterFormLayer,
        hideFilterFormLayer,
        filterRef,
        filterForm,
        setFilterForm,
        addVisible,
        isOverflow,
        setOverflow,
        showDeleteSavedFilterConfirm,
      }}>
      <div className="wrap_search">
        <strong className="screen_out">필터 검색</strong>
        <div className="group_adsearch">
          <div
            ref={clickRef}
            className="inner_adsearch"
            onClick={() => {
              if (!editable) {
                setAddVisible(true)
              }
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}>
            {addVisible || filters.length > 0 ? (
              <>
                <FilterWrapper ref={filterWrapRef}>
                  {filters.map(filter => (
                    <FilterChip
                      key={`${filter.domain}_${filter.key}`}
                      filter={filter}
                    />
                  ))}
                  {isOverflow && !addVisible && (
                    <li>
                      <NoneAnchor className="btn_admore">
                        <span className="ico_comm">더보기</span>
                      </NoneAnchor>
                    </li>
                  )}
                  {addVisible && <FilterAddInput ref={inputRef} />}
                </FilterWrapper>
                {filters.length > 0 && (
                  <>
                    <NoneAnchor
                      className="btn_adsave"
                      style={editable ? { pointerEvents: 'none' } : undefined}
                      onClick={() => {
                        if (!editable) {
                          setSavedFiltersCopy(savedFilters)
                          showFilterManageDialog()
                        }
                      }}>
                      <span className="inner_g">저장</span>
                    </NoneAnchor>
                    <NoneAnchor
                      className="btn_addel"
                      style={editable ? { pointerEvents: 'none' } : undefined}
                      onClick={() => {
                        if (!editable) {
                          batch(() => {
                            dispatch(clearFilters())
                            dispatch(campaignTableActions.setPage(0))
                            dispatch(adGroupTableActions.setPage(0))
                            dispatch(keywordTableActions.setPage(0))
                            dispatch(creativeTableActions.setPage(0))
                          })
                        }
                      }}>
                      <span className="ico_comm">전체 삭제</span>
                    </NoneAnchor>
                  </>
                )}
              </>
            ) : (
              <>
                <p className="lab_txt">
                  캠페인, 광고그룹, 키워드, 소재 검색 혹은 필터링
                </p>
                <NoneAnchor
                  className="btn_search"
                  style={editable ? { pointerEvents: 'none' } : undefined}>
                  <span className="ico_comm">검색</span>
                </NoneAnchor>
              </>
            )}
          </div>
        </div>
      </div>
      <FilterMenuLayer ref={menuLayerRef} inputRef={inputRef} />
      <FilterFormLayer ref={formLayerRef} />
      <FilterManageDialog type="basic_layer" className="savesearch_wrap">
        <FilterManageModal
          handleClose={hideFilterManageDialog}
          savedFilters={savedFiltersCopy}
        />
      </FilterManageDialog>
      <DeleteConfirm
        title="저장한 검색 조건 삭제"
        onClose={async ({ isConfirmed }) => {
          if (isConfirmed) {
            const { error, payload } = await dispatch(
              deleteFilter(deleteTarget.id)
            )
            if (error) {
              failWithErrorResponse(payload)
            } else {
              success('변경사항이 적용되었습니다.')
            }
          }
        }}>
        저장한 검색 조건 ‘{deleteTarget.name}’을 삭제하시겠습니까?
      </DeleteConfirm>
      {editable && hover && <FilterDisabledLayer filterRef={clickRef} />}
    </FilterContext.Provider>
  )
}
export default DashboardFilter
